import React from 'react';
import type { Conversation } from 'types/interfaces/message';
import { AvatarWithFlag } from 'components/AvatarWithFlag';
import { AssetUrls } from 'config/assets';
import { STATUS_TONE_CLASS, THREAD_TYPE_LABELS, THREAD_TYPE_STYLES, subjectTypeOf } from '../threadTypeStyles';
import { formatShortTime, rowSecondLine, rowTitle } from '../threadRowModel';

interface Props {
  row: Conversation;
  /** Checked in the bulk-select set. */
  selected: boolean;
  /** The thread open in the middle pane. */
  active: boolean;
  /** Mobile hides the checkbox; rows are tap targets only. */
  showCheckbox: boolean;
  onOpen: (conversationId: string) => void;
  onToggleSelect: (conversationId: string, e: React.MouseEvent<HTMLInputElement>) => void;
  onContextMenu: (conversationId: string, e: React.MouseEvent) => void;
  onArchive: (conversationId: string, e: React.MouseEvent) => void;
}

/**
 * One thread in the list (Decision 10): a 4px bar in the type color, the
 * subject (600 weight while unread), the red unread dot, the short time, and
 * "username · preview" underneath.
 */
const ThreadRow: React.FC<Props> = ({
  row,
  selected,
  active,
  showCheckbox,
  onOpen,
  onToggleSelect,
  onContextMenu,
  onArchive,
}) => {
  const subjectType = subjectTypeOf(row);
  const typeStyle = THREAD_TYPE_STYLES[subjectType];
  const unread = row.unreadCount > 0;
  const className = [
    'thread-row',
    unread ? 'unread' : '',
    selected ? 'selected' : '',
    active ? 'active' : '',
  ].filter(Boolean).join(' ');

  return (
    <div
      className={className}
      role="button"
      tabIndex={0}
      aria-label={`${THREAD_TYPE_LABELS[subjectType]} thread: ${rowTitle(row)}`}
      aria-current={active ? 'true' : undefined}
      onClick={() => onOpen(row.conversationId)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(row.conversationId);
        }
      }}
      onContextMenu={(e) => onContextMenu(row.conversationId, e)}
    >
      <span className="thread-row-bar" style={{ background: typeStyle.color }} aria-hidden="true" />
      {showCheckbox && (
        <span className="thread-row-select" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={selected}
            onClick={(e) => onToggleSelect(row.conversationId, e)}
            onChange={() => { /* handled in onClick so shift ranges work */ }}
            aria-label={`Select ${rowTitle(row)}`}
          />
        </span>
      )}
      <span className="thread-row-avatar">
        <AvatarWithFlag
          avatarUrl={row.otherUser.avatarUrl}
          username={row.otherUser.username}
          countryCode={row.otherUser.country}
          size={36}
          isElite={row.otherUser.isElite}
          showAnimatedBorder={row.otherUser.showAnimatedBorder}
          borderColor1={row.otherUser.borderColor1}
          borderColor2={row.otherUser.borderColor2}
        />
      </span>
      <span className="thread-row-body">
        <span className="thread-row-top">
          <span className="thread-row-title">{rowTitle(row)}</span>
          {unread && <span className="thread-row-unread-dot" aria-label={`${row.unreadCount} unread`} />}
          {row.status && (
            <span className={`thread-row-status status-badge ${STATUS_TONE_CLASS[row.status.tone]}`}>
              {row.status.label}
            </span>
          )}
          <span className="thread-row-time">{formatShortTime(row.lastMessageAt)}</span>
        </span>
        <span className="thread-row-preview">{rowSecondLine(row)}</span>
      </span>
      <button
        type="button"
        className="thread-row-archive"
        onClick={(e) => onArchive(row.conversationId, e)}
        title="Archive thread"
        aria-label="Archive thread"
      >
        <img src={AssetUrls.icons.dropdowns.close} alt="" style={{ width: 14, height: 14 }} />
      </button>
    </div>
  );
};

export default ThreadRow;
